// src/lib/menage-frise.js
//
// FRISE HORAIRE DE LA JOURNÉE (maquette validée Dimitri, 2026-08-10) — helpers
// PURS. Aucun accès réseau, aucun DOM, aucune écriture. Le rendu vit dans
// src/views/menage/FriseJournee.jsx ; les RÈGLES vivent ici, parce qu'une règle
// qui n'existe que dans du JSX ne peut être ni mesurée ni cassée exprès.
//
// CE QUE CE MODULE TRANCHE :
//   · la géométrie de l'axe (9 h → 18 h) et la position d'un instant ;
//   · la DURÉE d'un ménage — deux niveaux, exception puis défaut du logement ;
//   · le CRÉNEAU POSSIBLE d'un ménage (départ du partant → arrivée du suivant) ;
//   · le PLACEMENT d'un bloc dans ce créneau, aimanté à la demi-heure ;
//   · les CHEVAUCHEMENTS dans le couloir d'un agent ;
//   · la CHARGE d'un agent, interventions manuelles comprises ;
//   · ce qui vient APRÈS le ménage (attendu / arrivé / vide) ;
//   · la zone d'une demande d'horaire — ce qu'elle GRIGNOTERAIT.
//
// AUCUN MONTANT n'entre ici, ni n'en sort. Une demande d'horaire porte un prix
// côté serveur ; cet écran n'en connaît que l'heure.

/**
 * Bornes de l'axe : 9 h → 19 h (maquette 2026-08-11).
 *
 * La borne droite était à 18 h. Elle a été poussée d'une heure parce qu'une
 * arrivée à 17 h — l'horaire de Maison Zen et de ses chambres, soit quatre
 * logements du parc — tombait EXACTEMENT sur le bord : le trait et la carte de
 * l'après-ménage se retrouvaient collés au cadre, à moitié coupés, et rien ne
 * pouvait plus se dessiner « après ». Un axe qui s'arrête pile sur l'événement
 * le plus fréquent ne le montre pas, il le tronque.
 */
export const FRISE_DEBUT_MIN = 9 * 60
export const FRISE_FIN_MIN = 19 * 60
export const FRISE_AMPLITUDE_MIN = FRISE_FIN_MIN - FRISE_DEBUT_MIN

/** Heures graduées. Toutes les heures satureraient l'axe ; une sur deux se lit. */
export const HEURES_GRADUEES = [9, 11, 13, 15, 17]

/** Aimantation du glisser-déposer. La demi-heure est l'unité dont on parle sur le terrain. */
export const PAS_AIMANT_MIN = 30

/**
 * Durée d'un ménage quand rien n'est réglé. 90 min = la valeur déjà servie par
 * le guichet (`readMenageDureeMin`) — on ne crée pas un second défaut qui
 * divergerait du premier.
 */
export const DUREE_DEFAUT_MIN = 90

const TYPE_MENAGE = 'menage'
const TYPE_INTERVENTION = 'intervention'

// ── Temps ───────────────────────────────────────────────────────────────────

/** "HH:MM" → minutes depuis minuit. Tolère "9h", "9:5", "09h30". null si illisible. */
export function minutesDeHeure(hhmm) {
  if (typeof hhmm !== 'string') return null
  // Tolère aussi "HH:MM:SS" — c'est ce que PostgREST rend pour une colonne `time`.
  const m = hhmm.trim().match(/^(\d{1,2})\s*[:hH]\s*(\d{1,2})?(?::\d{1,2})?$/)
  if (!m) return null
  const h = Number(m[1])
  const mn = m[2] == null || m[2] === '' ? 0 : Number(m[2])
  if (!Number.isFinite(h) || !Number.isFinite(mn)) return null
  if (h < 0 || h > 23 || mn < 0 || mn > 59) return null
  return h * 60 + mn
}

/** minutes → "HH:MM". */
export function formaterHeure(min) {
  if (!Number.isFinite(min)) return '—'
  const borne = Math.max(0, Math.min(23 * 60 + 59, Math.round(min)))
  return `${String(Math.floor(borne / 60)).padStart(2, '0')}:${String(borne % 60).padStart(2, '0')}`
}

/** minutes → « 1 h 30 » / « 2 h » / « 45 min ». */
export function formaterDuree(min) {
  if (!Number.isFinite(min) || min <= 0) return '—'
  const h = Math.floor(min / 60)
  const m = Math.round(min % 60)
  if (h === 0) return `${m} min`
  return m > 0 ? `${h} h ${String(m).padStart(2, '0')}` : `${h} h`
}

/**
 * minutes → « 11h » / « 13h30 ». LE FORMAT DE LA FRISE, et le seul.
 *
 * ⚠️ Ni « 11:00 » ni « 11→15 » (exigence explicite 2026-08-11). « 11:00 » écrit
 * deux zéros qui n'apprennent rien et prend la place de ce qui compte ; la
 * flèche, elle, se lit comme un déplacement alors qu'il s'agit d'une plage.
 * Dans un bloc de 26 px de haut, chaque caractère économisé est un caractère
 * que le nom ou l'heure peut prendre.
 */
export function formaterHeureCourte(min) {
  if (!Number.isFinite(min)) return '—'
  const borne = Math.max(0, Math.min(23 * 60 + 59, Math.round(min)))
  const h = Math.floor(borne / 60)
  const m = borne % 60
  return m === 0 ? `${h}h` : `${h}h${String(m).padStart(2, '0')}`
}

/** « 11h - 15h » — les deux bornes d'un bloc, séparées par un tiret, jamais une flèche. */
export function libelleHeures(debut, fin) {
  return `${formaterHeureCourte(debut)} - ${formaterHeureCourte(fin)}`
}

/**
 * ⚠️ LARGEUR MINIMALE POUR « 4 h au lieu de 1 h 30 ».
 *
 * Vingt et un caractères à 8 px, plus la pastille (15 px), l'écart et les
 * marges : il faut environ 120 px, soit ~17 % d'un axe de 9 h → 19 h à la
 * largeur habituelle. En dessous, `overflow:hidden` couperait la phrase en
 * plein milieu — et « 4 h au lieu de 1 h… » se lit plus mal qu'une durée seule.
 */
export const SEUIL_EXCEPTION_LONGUE_PCT = 17

/**
 * CE QUE LE BLOC ÉCRIT À DROITE.
 *
 * Les heures, SAUF quand la durée déroge au défaut du logement : à ce
 * moment-là, « 4 h au lieu de 1 h 30 » vaut mieux que « 11h - 15h » (décision
 * Dimitri 2026-08-11). Les horaires restent lisibles à la position du bloc sur
 * l'axe ; l'exception, elle, n'est écrite nulle part ailleurs sur la frise.
 *
 * ⚠️ CALCULÉE SUR LA DURÉE COURANTE (`fin - debut`), pas sur `dureeMin` :
 * pendant qu'on étire le bloc, l'exception apparaît — ou disparaît quand on
 * revient au défaut — sous le doigt. Lire la valeur persistée afficherait
 * l'état d'avant le geste, c'est-à-dire un mensonge le temps du glissement.
 *
 * Une intervention n'a pas de défaut de logement : elle garde ses heures.
 */
export function texteDuBloc({ debut, fin, dureeParDefaut, type } = {}) {
  const heures = libelleHeures(debut, fin)
  if (type === TYPE_INTERVENTION) return heures
  const defaut = Number(dureeParDefaut)
  if (!Number.isFinite(defaut) || defaut <= 0) return heures
  const duree = fin - debut
  if (!Number.isFinite(duree) || duree === defaut) return heures
  return largeurPct(debut, fin) >= SEUIL_EXCEPTION_LONGUE_PCT
    ? `${formaterDuree(duree)} au lieu de ${formaterDuree(defaut)}`
    // Trop étroit pour la phrase : la durée seule, jamais une phrase coupée.
    : formaterDuree(duree)
}

// ── Géométrie de l'axe ──────────────────────────────────────────────────────

/**
 * Position d'un instant sur l'axe, en pourcentage. CLAMPÉE aux bornes : un
 * ménage qui déborderait de l'axe doit rester visible à son bord, jamais sortir
 * du cadre — un bloc invisible se lit comme un bloc absent.
 */
export function pctDeMinutes(min) {
  if (!Number.isFinite(min)) return null
  const borne = Math.max(FRISE_DEBUT_MIN, Math.min(FRISE_FIN_MIN, min))
  return ((borne - FRISE_DEBUT_MIN) / FRISE_AMPLITUDE_MIN) * 100
}

/** Largeur en % entre deux instants. Jamais négative ; 0 si l'ordre est inversé. */
export function largeurPct(debut, fin) {
  const a = pctDeMinutes(debut)
  const b = pctDeMinutes(fin)
  if (a == null || b == null) return 0
  return Math.max(0, b - a)
}

/** Inverse de `pctDeMinutes` — sert au glisser-déposer (position souris → heure). */
export function minutesDePct(pct) {
  if (!Number.isFinite(pct)) return null
  const p = Math.max(0, Math.min(100, pct))
  return FRISE_DEBUT_MIN + (p / 100) * FRISE_AMPLITUDE_MIN
}

/** Aimante une minute au pas donné (demi-heure par défaut). */
export function aimanter(min, pas = PAS_AIMANT_MIN) {
  if (!Number.isFinite(min)) return null
  const p = Number.isFinite(pas) && pas > 0 ? pas : PAS_AIMANT_MIN
  return Math.round(min / p) * p
}

// ── LE REMPLISSAGE D'UN BLOC — ce qui est écoulé, ce qui reste ──────────────

/**
 * QUELLE PART DU BLOC EST DERRIÈRE NOUS, en pourcentage de SA PROPRE durée.
 *
 * ⚠️ Ce n'est PAS une position sur l'axe : c'est une proportion interne au
 * bloc. Un ménage de 11 h à 15 h vu à 13 h est à 50 % — quelle que soit sa
 * place sur la frise. Confondre les deux dessinerait un dégradé qui bouge
 * quand on change les bornes de l'axe.
 *
 * ⚠️ LA MÊME RÉFÉRENCE POUR TOUS LES BLOCS. `nowMin` est calculé une fois et
 * descendu ; aucun bloc ne relit l'horloge pour son compte. C'est la règle qui
 * a manqué au défaut du 2026-08-10 (un seul bloc rougissait alors que
 * plusieurs heures étaient passées).
 *
 * `position` dit où se situe le JOUR REGARDÉ, parce que `nowMin` ne veut rien
 * dire ailleurs qu'aujourd'hui : un jour passé est entièrement derrière nous
 * (100 %), un jour futur entièrement devant (0 %). Sans ça, naviguer à demain
 * peindrait la matinée comme si elle était déjà faite.
 *
 * @param {'passe'|'aujourdhui'|'futur'} position
 * @returns {number} 0 → pas commencé · 100 → terminé
 */
export function avancementPct({ debut, fin }, nowMin, position = 'aujourdhui') {
  if (position === 'passe') return 100
  if (position === 'futur') return 0
  if (!Number.isFinite(debut) || !Number.isFinite(fin) || fin <= debut) return 0
  if (!Number.isFinite(nowMin)) return 0
  if (nowMin <= debut) return 0
  if (nowMin >= fin) return 100
  return ((nowMin - debut) / (fin - debut)) * 100
}

/**
 * LE DÉGRADÉ NET — foncé jusqu'à l'heure courante, ciel ensuite.
 *
 * Deux arrêts de couleur au MÊME pourcentage : la transition est une frontière,
 * pas un fondu. Un dégradé progressif ferait croire à une incertitude sur
 * l'heure alors qu'on la connaît à la minute.
 */
export function degradeBloc(pct, fonce = '#3d5166', ciel = '#87b8e6') {
  const x = Math.max(0, Math.min(100, Number.isFinite(pct) ? pct : 0))
  if (x <= 0) return ciel
  if (x >= 100) return fonce
  return `linear-gradient(to right, ${fonce} 0%, ${fonce} ${x}%, ${ciel} ${x}%, ${ciel} 100%)`
}

// ── Durée d'un ménage — DEUX NIVEAUX ────────────────────────────────────────

/**
 * DEUX NIVEAUX, comme les règles d'assignation : une valeur par défaut posée
 * une fois sur le logement, une exception ponctuelle posée sur CE ménage-là.
 *
 * L'exception NE TOUCHE JAMAIS la règle du logement — c'est tout l'intérêt :
 * « il y a eu une fête, 4 h aujourd'hui » ne doit pas rallonger les six mois
 * suivants. L'écran doit le dire au moment où on la pose.
 *
 * Ordre : exception du ménage → défaut du logement → DUREE_DEFAUT_MIN.
 * Une valeur ≤ 0 ou illisible est ignorée à chaque étage (jamais un bloc de
 * largeur nulle, qui serait indéplaçable et invisible).
 */
export function dureeDuMenage(run, logement) {
  const lire = (v) => {
    const n = Number(v)
    return Number.isFinite(n) && n > 0 ? n : null
  }
  return lire(run?.duree_min) ?? lire(logement?.menage_duree_min) ?? DUREE_DEFAUT_MIN
}

/** Le ménage porte-t-il une exception de durée ? (pilote la mention à l'écran). */
export function aUneDureeException(run, logement) {
  const n = Number(run?.duree_min)
  if (!Number.isFinite(n) || n <= 0) return false
  return n !== (Number(logement?.menage_duree_min) > 0 ? Number(logement.menage_duree_min) : DUREE_DEFAUT_MIN)
}

/** Durée qui s'appliquera aux PROCHAINS ménages du logement (phrase de rappel). */
export function dureeParDefautDuLogement(logement) {
  const n = Number(logement?.menage_duree_min)
  return Number.isFinite(n) && n > 0 ? n : DUREE_DEFAUT_MIN
}

// ── Créneau possible ────────────────────────────────────────────────────────

/**
 * Le ménage ne peut avoir lieu qu'ENTRE le départ du voyageur partant et
 * l'arrivée du suivant. C'est le fond bleu pâle de la vue par agent : on ne
 * peut pas en sortir.
 *
 * Départ inconnu → on part du bord gauche de l'axe (le logement est libre au
 * plus tôt). Arrivée inconnue → on va jusqu'au bord droit : personne n'arrive,
 * donc rien ne presse. Dans les deux cas on ouvre plutôt qu'on ne ferme —
 * fermer sur une donnée absente inventerait une contrainte.
 */
export function creneauPossible({ checkout, checkin } = {}) {
  const co = minutesDeHeure(checkout)
  const ci = minutesDeHeure(checkin)
  const debut = co == null ? FRISE_DEBUT_MIN : Math.max(FRISE_DEBUT_MIN, co)
  const fin = ci == null ? FRISE_FIN_MIN : Math.min(FRISE_FIN_MIN, ci)
  // ⚠️ BORNE CONNUE ≠ BORNE PAR DÉFAUT (panneau v3, 2026-08-19) : sans
  // horaire, le créneau retombe sur le cadre 9 h-19 h de la frise — un repli
  // de DESSIN, pas une heure de séjour. `departConnu`/`arriveeConnue` disent
  // la différence, pour que les cartes séjour puissent dire « heure
  // inconnue » plutôt qu'afficher le cadre comme un fait.
  const departConnu = co != null
  const arriveeConnue = ci != null
  // Données aberrantes (arrivée avant départ) : on ne rend pas un créneau
  // négatif, on rend la journée entière et le désordre restera visible ailleurs.
  if (fin <= debut) {
    return { debut: FRISE_DEBUT_MIN, fin: FRISE_FIN_MIN, degrade: true, departConnu, arriveeConnue }
  }
  return { debut, fin, degrade: false, departConnu, arriveeConnue }
}

/**
 * Borne un début de bloc dans son créneau, en tenant compte de sa durée : le
 * bloc doit tenir ENTIER dedans. Si la durée excède le créneau, on colle à
 * gauche — le débordement reste visible plutôt que d'être écrasé en silence.
 */
export function bornerDebut(debut, duree, creneau) {
  if (!creneau || !Number.isFinite(debut)) return creneau?.debut ?? FRISE_DEBUT_MIN
  const max = creneau.fin - duree
  if (max <= creneau.debut) return creneau.debut
  return Math.max(creneau.debut, Math.min(max, debut))
}

/**
 * Place un bloc : l'heure FIXÉE par l'orchestrateur si elle existe, sinon le
 * début du créneau. Toujours aimantée et bornée.
 */
export function placerBloc({ heureFixee, creneau, duree }) {
  const souhaite = minutesDeHeure(heureFixee)
  const brut = souhaite == null ? creneau.debut : souhaite
  const debut = bornerDebut(aimanter(brut), duree, creneau)
  // ⚠️ ON DIT QUAND ON A RECALÉ. Le bornage existait déjà — le bloc rentrait
  // dans la plage sans bruit. Mais depuis que les horaires ACCORDÉS déplacent
  // la plage (2026-08-12), une heure fixée à 11 h peut se retrouver hors jeu
  // parce qu'un départ tardif a été accordé APRÈS. Recaler en silence
  // reviendrait à défaire la décision de l'orchestrateur sans qu'il l'apprenne :
  // il croirait son ménage à 11 h alors qu'il commence à 13 h.
  const recale = souhaite != null && debut !== aimanter(souhaite)
  return { debut, fin: debut + duree, recale, initiale: souhaite }
}

/**
 * L'HEURE À LAQUELLE L'AGENT DOIT Y ALLER — la seule qui ait du sens pour lui.
 *
 * ⚠️ ON RECALE, ON NE FAIT PAS ARBITRER. Première version : on affichait la
 * contradiction à l'agent (« cette heure n'est plus possible, prévenez
 * l'orchestrateur »). C'était lui demander de trancher entre deux consignes,
 * puis de faire le facteur. Sofia doit savoir QUAND y aller, point. L'alerte,
 * elle, remonte à celui qui décide — voir `motifDeLigne`, qui fait remonter la
 * ligne dans « À régler » avec le motif « heure recalée à 13h ».
 *
 * Même bornage que la frise (`bornerDebut`), pour que les deux écrans ne
 * puissent pas afficher deux heures différentes du même ménage.
 *
 * @returns {{debut:number|null, recale:boolean}}
 */
export function heureEffective({ heureFixee, dureeMin, plageDebut, plageFin } = {}) {
  const brut = minutesDeHeure(typeof heureFixee === 'string' ? heureFixee.slice(0, 5) : heureFixee)
  if (brut == null) return { debut: null, recale: false }
  const duree = Number(dureeMin) > 0 ? Number(dureeMin) : DUREE_DEFAUT_MIN
  const pd = minutesDeHeure(typeof plageDebut === 'string' ? plageDebut.slice(0, 5) : plageDebut)
  const pf = minutesDeHeure(typeof plageFin === 'string' ? plageFin.slice(0, 5) : plageFin)
  // Sans plage connue, on ne recale rien : borner sur une donnée absente
  // déplacerait un ménage sans raison.
  if (pd == null || pf == null) return { debut: brut, recale: false }
  const debut = bornerDebut(brut, duree, { debut: pd, fin: pf })
  return { debut, recale: debut !== brut }
}

// ── Chevauchements ──────────────────────────────────────────────────────────

/**
 * Deux blocs qui se chevauchent dans le couloir d'un agent = il ne peut pas
 * être aux deux endroits. Renvoie l'ensemble des id EN CONFLIT.
 *
 * Comparaison en intervalles SEMI-OUVERTS : un ménage qui finit à 13:30 et le
 * suivant qui commence à 13:30 s'enchaînent, ils ne se chevauchent pas. Traiter
 * le contact comme un conflit rendrait toute journée dense rouge en permanence,
 * et le signal ne voudrait plus rien dire.
 */
export function detecterChevauchements(blocs) {
  const list = Array.isArray(blocs) ? blocs.filter(b => b && Number.isFinite(b.debut) && Number.isFinite(b.fin)) : []
  const enConflit = new Set()
  for (let i = 0; i < list.length; i += 1) {
    for (let j = i + 1; j < list.length; j += 1) {
      const a = list[i]
      const b = list[j]
      if (a.debut < b.fin && b.debut < a.fin) {
        enConflit.add(a.id)
        enConflit.add(b.id)
      }
    }
  }
  return enConflit
}

// ── Charge d'un agent ───────────────────────────────────────────────────────

/**
 * « 7 h sur 6 h » : le premier nombre est le travail à faire, le second la
 * FENÊTRE dans laquelle il doit tenir — du plus tôt où il peut commencer au
 * plus tard où il doit avoir fini. Comparer le travail à une journée théorique
 * de 8 h ne dirait rien : ce qui coince, c'est l'imbrication des créneaux.
 *
 * LES INTERVENTIONS MANUELLES COMPTENT. Un état des lieux de 2 h occupe l'agent
 * autant qu'un ménage ; les exclure ferait mentir la vue de charge, qui est
 * précisément ce qu'on vient y chercher.
 *
 * Les ménages TERMINÉS comptent aussi : ils ont bien occupé l'agent.
 */
export function chargeAgent(blocs) {
  const list = Array.isArray(blocs) ? blocs : []
  if (list.length === 0) return { travailMin: 0, fenetreMin: 0, depassement: false, vide: true }
  let travailMin = 0
  let tot = Infinity
  let tard = -Infinity
  for (const b of list) {
    const duree = Number(b?.dureeMin)
    if (Number.isFinite(duree) && duree > 0) travailMin += duree
    const cd = b?.creneau?.debut
    const cf = b?.creneau?.fin
    if (Number.isFinite(cd)) tot = Math.min(tot, cd)
    if (Number.isFinite(cf)) tard = Math.max(tard, cf)
  }
  const fenetreMin = Number.isFinite(tot) && Number.isFinite(tard) && tard > tot ? tard - tot : 0
  return {
    travailMin,
    fenetreMin,
    depassement: fenetreMin > 0 && travailMin > fenetreMin,
    vide: false,
  }
}

/** « 7 h sur 6 h » / « libre ». */
export function libelleCharge(charge) {
  if (!charge || charge.vide) return 'libre'
  if (!charge.fenetreMin) return formaterDuree(charge.travailMin)
  return `${formaterDuree(charge.travailMin)} sur ${formaterDuree(charge.fenetreMin)}`
}

// ── Ce qui vient APRÈS le ménage ────────────────────────────────────────────

/**
 * Dit si un retard est grave. Un ménage en retard sur un logement où personne
 * n'arrive n'est pas urgent ; le même retard avec trois voyageurs déjà sur le
 * palier l'est. Sans cette information, toutes les alertes se valent — et des
 * alertes qui se valent toutes ne hiérarchisent rien.
 *
 * @returns {'vide'|'arrive'|'attendu'}
 */
export function etatApres({ checkin, arriveeReelle, nowMin } = {}) {
  const ci = minutesDeHeure(checkin)
  const reelle = minutesDeHeure(arriveeReelle)
  // Aucune arrivée connue, ni théorique ni réelle → personne après.
  if (ci == null && reelle == null) return 'vide'
  // L'arrivée RÉELLE fait foi dès qu'elle est franchie (ADR-226 : réservée à
  // l'orchestrateur, ce qui est exactement le rôle qui lit cet écran).
  if (reelle != null && Number.isFinite(nowMin) && nowMin >= reelle) return 'arrive'
  return 'attendu'
}

// ── Demande d'horaire : ce qu'elle GRIGNOTERAIT ─────────────────────────────

/**
 * LA BANDE D'UNE DEMANDE — sur sa PROPRE ligne, jamais sur le bloc.
 *
 * ⚠️ CE QUI CHANGE LE 2026-08-11, ET POURQUOI. La demande était dessinée EN
 * SUPERPOSITION du bloc de ménage, hachurée par-dessus. Deux informations
 * différentes occupaient le même rectangle : on ne lisait plus ni les heures du
 * ménage, ni l'étendue de la demande. La bande descend donc d'un cran (9 px de
 * haut, sous le bloc), et l'heure s'écrit À CÔTÉ d'elle — 9 px ne peuvent pas
 * porter de texte, et un texte qu'on ne peut pas lire n'est pas une information.
 *
 * ⚠️ UNE DEMANDE N'EST PAS UN PROBLÈME. Elle ne prend ni bordure rouge ni fond
 * rosé : personne n'a rien raté, un voyageur a demandé quelque chose.
 *
 * · Départ tardif : le partant resterait plus longtemps → la bande va du DÉBUT
 *   de la plage à l'heure demandée, et le libellé se pose à sa DROITE.
 * · Arrivée anticipée : le suivant arriverait plus tôt → la bande va de l'heure
 *   demandée à la FIN de la plage, et le libellé se pose à sa GAUCHE.
 *
 * @returns {{debut:number, fin:number, libelle:string, cote:'gauche'|'droite'}|null}
 */
export function bandeDemande(demande, creneau) {
  if (!demande || !creneau) return null
  const heure = minutesDeHeure(demande.requested_time)
  if (heure == null) return null
  if (demande.event_kind === 'early_checkin') {
    if (heure >= creneau.fin) return null // n'ampute rien
    return {
      debut: Math.max(creneau.debut, heure), fin: creneau.fin,
      libelle: `← dès ${formaterHeureCourte(heure)}`, cote: 'gauche',
    }
  }
  // late_checkout (et tout autre sens : le départ est le cas par défaut)
  if (heure <= creneau.debut) return null
  return {
    debut: creneau.debut, fin: Math.min(creneau.fin, heure),
    libelle: `jusqu'à ${formaterHeureCourte(heure)} →`, cote: 'droite',
  }
}

/**
 * LA PHRASE COMPLÈTE, celle de la colonne de gauche.
 *
 * Elle dit qui veut quoi, en toutes lettres. La bande montre l'étendue, la
 * phrase donne le sens : « ← dès 14h » seul obligerait à deviner s'il s'agit
 * d'une arrivée, d'un départ ou d'une contrainte de l'agent.
 */
export function phraseDemande(demande) {
  if (!demande) return null
  const heure = minutesDeHeure(demande.requested_time)
  if (heure == null) return null
  const h = formaterHeureCourte(heure)
  return demande.event_kind === 'early_checkin'
    ? `Le voyageur veut entrer dès ${h}`
    : `Le voyageur veut rester jusqu'à ${h}`
}

// ── Gestes : étirer, déplacer, tracer ───────────────────────────────────────

/** Durée minimale d'un bloc. Un pas d'aimantation — en dessous, on ne vise plus. */
export const DUREE_MIN_BLOC = PAS_AIMANT_MIN

/**
 * ÉTIRER par un bord. Le bloc BUTE sur la plage, il n'y déborde pas et ne
 * « passe pas en rouge » : la plage est une contrainte physique (le voyageur
 * part, le suivant arrive), pas une faute de l'utilisateur. Colorer en rouge une
 * limite qu'on ne peut pas franchir apprend à ignorer le rouge.
 *
 * @param {'gauche'|'droite'} bord
 * @returns {{debut:number, fin:number, aBute:boolean}}
 */
export function etirerBloc({ bord, minuteVisee, debut, fin, creneau }) {
  const c = creneau || { debut: FRISE_DEBUT_MIN, fin: FRISE_FIN_MIN }
  const vise = aimanter(minuteVisee)
  if (bord === 'gauche') {
    const max = fin - DUREE_MIN_BLOC
    const borne = Math.max(c.debut, Math.min(max, vise))
    return { debut: borne, fin, aBute: borne !== vise }
  }
  const min = debut + DUREE_MIN_BLOC
  const borne = Math.min(c.fin, Math.max(min, vise))
  return { debut, fin: borne, aBute: borne !== vise }
}

/**
 * TRACER un bloc sur une ligne « heure à fixer » : premier point = début, on
 * tire, on relâche. L'ordre des deux points n'a pas d'importance — tracer de
 * droite à gauche est un geste naturel qu'il serait absurde de refuser.
 */
export function tracerBloc({ point1, point2, creneau }) {
  const c = creneau || { debut: FRISE_DEBUT_MIN, fin: FRISE_FIN_MIN }
  const a = aimanter(Math.min(point1, point2))
  const b = aimanter(Math.max(point1, point2))
  const debut = Math.max(c.debut, Math.min(a, c.fin - DUREE_MIN_BLOC))
  const fin = Math.min(c.fin, Math.max(b, debut + DUREE_MIN_BLOC))
  return { debut, fin }
}

/**
 * Un mouvement de moins de 4 px est un CLIC, pas un glissement.
 *
 * Sans ce seuil, ouvrir le menu des agents deviendrait impossible : la main
 * bouge toujours d'un ou deux pixels entre l'appui et le relâchement, et chaque
 * clic serait interprété comme un déplacement d'une minute.
 */
export const SEUIL_CLIC_PX = 4
export function estUnClic(dx, dy, seuil = SEUIL_CLIC_PX) {
  return Math.abs(dx) < seuil && Math.abs(dy) < seuil
}

// ── État d'un bloc ──────────────────────────────────────────────────────────

/**
 * L'état qui pilote la couleur et le libellé.
 *
 * ⚠️ « sans agent » et « heure à fixer » sont DEUX états différents, et les
 * confondre serait un contresens : le premier attend qu'on décide QUI, le
 * second qu'on décide QUAND. Les actions à prendre ne sont pas les mêmes.
 */
export function etatBloc(bloc, { enRetard = false } = {}) {
  if (!bloc) return 'normal'
  // 🔴 « FAIT » PRIME SUR LE TYPE. L'ancien ordre court-circuitait sur
  // TYPE_INTERVENTION avant de regarder `termine` : une livraison validée par
  // l'agent restait peinte comme À FAIRE — le retour du terrain existait en
  // base et mourait ici, à une comparaison près (mesuré au scénario de
  // communication, 2026-08-14). Fait = même langage partout, ménage ou pas.
  if (bloc.termine) return 'valide'
  if (bloc.type === TYPE_INTERVENTION) return 'intervention'
  if (!bloc.agent) return 'sans_agent'
  if (!bloc.aHeureFixee) return 'heure_a_fixer'
  if (enRetard) return 'en_retard'
  return 'normal'
}

// ── Ce qui vient APRÈS : l'heure ANNONCÉE prime sur l'heure du logement ─────

/**
 * L'heure d'arrivée du voyageur suivant.
 *
 * ⚠️ ELLE VIENT DU FORMULAIRE DE BIENVENUE quand il a été rempli, PAS de
 * l'horaire du logement. Un logement affiche 17:00 parce que c'est sa règle ;
 * le voyageur, lui, a écrit qu'il arrivait à 13:00. Organiser la journée sur la
 * règle quand on connaît le fait, c'est se tromper volontairement.
 *
 * Quand le formulaire n'est pas rempli, on retombe sur l'horaire du logement —
 * et `source` permet à l'écran de LE DIRE, au lieu de faire passer une règle
 * pour une annonce.
 *
 * @returns {{minutes:number, source:'formulaire'|'logement'}|null}
 */
export function arriveeAffichee({ arriveeFormulaire, checkinLogement } = {}) {
  const f = minutesDeHeure(arriveeFormulaire)
  if (f != null) return { minutes: f, source: 'formulaire' }
  const c = minutesDeHeure(checkinLogement)
  if (c != null) return { minutes: c, source: 'logement' }
  return null
}

/**
 * LE VOYAGEUR EST-IL DÉJÀ LÀ ?
 *
 * ⚠️ UNE SEULE CONDITION : l'heure d'arrivée est passée. Sans exception.
 *
 * La première version exigeait en plus que l'heure vienne du FORMULAIRE — et
 * ça produisait exactement le défaut signalé le 2026-08-10 : un seul bloc
 * rougissait, les autres restaient bleus alors que leur heure était tout aussi
 * dépassée. La provenance de l'heure dit avec quelle CERTITUDE on la connaît
 * (c'est le rôle de `source`, qui pilote le pointillé et l'infobulle) ; elle ne
 * dit rien sur le fait que l'heure soit passée. Mélanger les deux revenait à
 * décider qu'un voyageur non annoncé n'arrive jamais.
 *
 * @returns {boolean} false si l'heure est inconnue ou si `nowMin` l'est —
 *          jamais « arrivé » par défaut : on n'invente pas une présence.
 */
/**
 * L'HEURE À LAQUELLE ON COMPARE — une seule, pour tout l'écran.
 *
 * ⚠️ `nowMin` ne veut rien dire ailleurs qu'aujourd'hui. Sur un jour PASSÉ,
 * toutes les arrivées sont derrière nous ; sur un jour FUTUR, aucune ne l'est.
 * Sans ce virage, naviguer à demain peindrait en rouge des voyageurs qui ne
 * sont pas encore partis de chez eux, et naviguer à hier laisserait des
 * arrivées « attendues » à jamais.
 */
export function heureDeReference(nowMin, position = 'aujourdhui') {
  if (position === 'passe') return 24 * 60
  if (position === 'futur') return -1
  return Number.isFinite(nowMin) ? nowMin : -1
}

export function estDejaArrive(arrivee, nowMin) {
  if (!arrivee || !Number.isFinite(arrivee.minutes)) return false
  if (!Number.isFinite(nowMin)) return false
  return nowMin >= arrivee.minutes
}

/**
 * CE QUI EST ÉCRIT SUR LA CARTE DE L'APRÈS-MÉNAGE.
 *
 * ⚠️ « pers. » TOUJOURS, jamais un nombre nu (exigence 2026-08-11) : « 17h · 3 »
 * se lit comme une troisième information non identifiée — un numéro de
 * logement, un nombre de nuits, un étage. Trois caractères lèvent l'ambiguïté.
 *
 * @returns {{texte:string, etat:'vide'|'arrive'|'attendu'}}
 */
export function libelleApres(arrivee, dejaArrive, personnes) {
  if (!arrivee || !Number.isFinite(arrivee.minutes)) {
    return { texte: 'personne après', etat: 'vide' }
  }
  const h = formaterHeureCourte(arrivee.minutes)
  const qui = Number.isFinite(Number(personnes)) && Number(personnes) > 0
    ? ` · ${Number(personnes)} pers.`
    : ''
  return dejaArrive
    ? { texte: `Arrivé ${h}${qui}`, etat: 'arrive' }
    : { texte: `${h}${qui}`, etat: 'attendu' }
}

// ── CONFLIT D'AGENT — CROISÉ ENTRE TOUTES LES LIGNES ────────────────────────

/**
 * ⚠️ LE DÉFAUT QUE CECI CORRIGE. Dans la vue par logement, chaque ligne est un
 * LOGEMENT : deux ménages du même agent au même moment vivent donc sur deux
 * lignes différentes, et aucune comparaison intra-ligne ne peut les voir. Il
 * faut regrouper TOUS les blocs par agent, toutes lignes confondues.
 *
 * Le résultat est le même quelle que soit la vue : si un agent est en conflit,
 * il l'est partout. Une alerte qui dépend de l'onglet ouvert n'est pas une
 * alerte, c'est une coïncidence.
 *
 * Les interventions COMPTENT : elles occupent l'agent autant qu'un ménage.
 *
 * ⚠️ LES MÉNAGES VALIDÉS SONT EXCLUS (arbitrage 2026-08-10, après constat à
 * l'écran). Ils l'étaient d'abord inclus — « deux ménages simultanés restent
 * une journée impossible ». Mais un ménage validé est FAIT : on ne peut plus le
 * déplacer, et son bloc n'est qu'une approximation de ce qui a réellement eu
 * lieu. Le signaler transformait une alerte actionnable en observation
 * historique, et faisait rougir un agent dont la journée ne posait aucun
 * problème. Un rouge qui désigne ce sur quoi on ne peut pas agir n'aide pas.
 *
 * @returns {{ ids: Set<string>, parAgent: Map<string, Array> }}
 */
export function detecterConflitsParAgent(blocs) {
  const list = Array.isArray(blocs) ? blocs : []
  const parAgent = new Map()
  for (const b of list) {
    if (!b || !b.agent) continue // « personne » ne peut pas être en conflit avec personne
    // ⚠️ LES VALIDÉS OCCUPENT AUSSI (bug Dimitri 2026-08-17) : un ménage fait
    // a occupé la personne — elle ne peut pas être à deux endroits, que ce
    // soit fait ou pas. Le détecteur compte TOUS les états.
    if (!Number.isFinite(b.debut) || !Number.isFinite(b.fin)) continue
    if (!parAgent.has(b.agent)) parAgent.set(b.agent, [])
    parAgent.get(b.agent).push(b)
  }
  const ids = new Set()
  const conflitsParAgent = new Map()
  for (const [agent, liste] of parAgent) {
    const enConflit = detecterChevauchements(liste)
    if (enConflit.size === 0) continue
    conflitsParAgent.set(agent, liste.filter((b) => enConflit.has(b.id)))
    for (const id of enConflit) ids.add(id)
  }
  return { ids, parAgent: conflitsParAgent }
}

/** « deux », « trois »… Un nombre écrit se lit dans une phrase ; un chiffre l'interrompt. */
export function enLettres(n) {
  return ({ 2: 'deux', 3: 'trois', 4: 'quatre', 5: 'cinq', 6: 'six' })[n] || String(n)
}

/**
 * LA POINTE D'UN CONFLIT : combien de ménages EN MÊME TEMPS, et QUAND.
 *
 * ⚠️ « à la fois » se compte par SIMULTANÉITÉ MAXIMALE, pas par nombre de blocs
 * impliqués. Trois ménages qui se chevauchent deux par deux, jamais tous les
 * trois ensemble, font un conflit « sur 2 à la fois » : dire 3 gonflerait le
 * problème, et la fenêtre annoncée ne correspondrait à rien de réel.
 *
 * Balayage classique : +1 à chaque début, −1 à chaque fin, on retient le
 * sommet. Les fins sont traitées AVANT les débuts à heure égale — un ménage qui
 * finit à 13:30 et le suivant qui commence à 13:30 s'enchaînent (mêmes
 * intervalles semi-ouverts que `detecterChevauchements`, sinon la règle
 * changerait selon l'endroit du code qui la pose).
 *
 * @returns {{n:number, debut:number, fin:number}|null}
 */
export function pointeConflit(blocs) {
  const list = (Array.isArray(blocs) ? blocs : [])
    .filter((b) => b && Number.isFinite(b.debut) && Number.isFinite(b.fin) && b.fin > b.debut)
  if (list.length === 0) return null
  const evts = []
  for (const b of list) {
    evts.push({ t: b.debut, d: 1 })
    evts.push({ t: b.fin, d: -1 })
  }
  evts.sort((x, y) => x.t - y.t || x.d - y.d) // −1 avant +1 à heure égale
  let courant = 0
  let best = null
  for (let i = 0; i < evts.length; i += 1) {
    courant += evts[i].d
    const suivant = evts[i + 1]
    if (!suivant || suivant.t === evts[i].t) continue
    if (courant >= 2 && (!best || courant > best.n)) {
      best = { n: courant, debut: evts[i].t, fin: suivant.t }
    }
  }
  return best
}

/** Le motif de ligne d'un conflit : « Karim sur 3 à la fois » — jamais « en double ». */
export function libelleConflit(prenom, blocsDeLAgent) {
  const pointe = pointeConflit(blocsDeLAgent)
  const n = pointe ? pointe.n : 2
  return `${prenom} sur ${n} à la fois`
}

/**
 * Message qui NOMME l'agent, COMPTE les endroits et DIT quand.
 *
 * ⚠️ Le message disait « est sur deux ménages en même temps » en dur. Constaté
 * à l'écran le 2026-08-11 : Karim en cumulait TROIS, et la phrase sous-estimait
 * ce qu'elle signalait. Un compteur figé dans une phrase finit toujours par
 * mentir — il est donc calculé.
 */
// C-1 (2026-08-19) : repli d'affichage PUR — la partie avant l'arobase,
// capitalisée. (menage-frise est une lib pure : pas d'import de composant.)
function formaterPrenomLocal(email) {
  const local = String(email || '').split('@')[0]
  if (!local) return 'Agent'
  const tok = (local.split(/[._+-]/).filter(Boolean)[0] || local).replace(/[0-9]+/g, '')
  const base = tok || local
  return base.charAt(0).toUpperCase() + base.slice(1).toLowerCase()
}

export function messageConflitAgent(conflitsParAgent, nomDe) {
  if (!conflitsParAgent || conflitsParAgent.size === 0) return null
  const nom = typeof nomDe === 'function' ? nomDe : (e) => e
  const parts = [...conflitsParAgent.entries()].map(([email, blocs]) => {
    // C-1 : jamais l'email brut — le prénom se dérive du local.
    const qui = nom(email) || formaterPrenomLocal(email)
    const pointe = pointeConflit(blocs)
    if (!pointe) return `${qui} est sur deux ménages en même temps`
    const quand = `entre ${formaterHeureCourte(pointe.debut)} et ${formaterHeureCourte(pointe.fin)}`
    return `${qui} ne peut pas être à ${enLettres(pointe.n)} endroits ${quand}`
  })
  return parts.join(' · ')
}

/**
 * QUI EST LIBRE, ET À PARTIR DE QUAND — la moitié utile du message de conflit.
 *
 * Dire « Karim est pris » sans dire vers qui se tourner laisse le travail
 * entier à faire. On ne propose QUE les agents qui ne sont pas eux-mêmes en
 * conflit : renvoyer vers quelqu'un de déjà débordé déplacerait le problème.
 *
 * « libre après H » = la fin de son dernier bloc. Un agent sans aucun bloc est
 * libre tout de suite — on le dit avec le mot « maintenant », pas avec une
 * heure qui ferait croire à une contrainte.
 *
 * @returns {string|null} « Nadia est libre après 15h · Sofia après 15h »
 */
export function messageAgentsLibres(blocs, agents, { exclure = new Set(), prenomDe } = {}) {
  const nom = typeof prenomDe === 'function' ? prenomDe : (e) => e
  const list = Array.isArray(blocs) ? blocs : []
  const libres = (Array.isArray(agents) ? agents : [])
    .filter((e) => e && !exclure.has(e))
    .map((email) => {
      const siens = list.filter((b) => b?.agent === email && Number.isFinite(b.fin))
      const fin = siens.length ? Math.max(...siens.map((b) => b.fin)) : null
      return { email, fin }
    })
  if (libres.length === 0) return null
  return libres
    .map(({ email, fin }, i) => {
      const quand = fin == null ? 'maintenant' : `après ${formaterHeureCourte(fin)}`
      return i === 0 ? `${nom(email)} est libre ${quand}` : `${nom(email)} ${quand}`
    })
    .join(' · ')
}

/**
 * LE MOTIF D'UNE LIGNE — ce qu'elle demande, écrit en toutes lettres.
 *
 * Remplace le codage par la couleur (maquette 2026-08-10) : un liseré, un badge
 * et trois nuances de rosé obligeaient à traduire une palette avant de
 * comprendre. Un mot se lit sans traduction, et un seul rosé suffit alors à
 * dire « cette ligne demande quelque chose ».
 *
 * ORDRE DE PRIORITÉ, et il n'est pas arbitraire :
 *   1. SANS AGENT — rien ne peut avancer tant que personne n'est désigné ;
 *   2. « X est pris » — il y a bien un agent, mais il est ailleurs au même
 *      moment ; on NOMME celui qui bloque, sinon on cherche qui ;
 *   3. NON VALIDÉ — le ménage a peut-être été fait, il manque la validation.
 * Les trois peuvent coexister ; on affiche celui sur lequel on agit d'abord.
 *
 * @returns {string|null} null = la ligne ne demande rien, et ne doit alors
 *          porter NI point, NI texte, NI fond rosé.
 */
export function motifDeLigne(bloc, {
  enRetard = false, blocsDeLAgentEnConflit = null, prenomDe, demande = null,
  nowMin, position = 'aujourdhui',
} = {}) {
  if (!bloc) return null
  // 1. PERSONNE — et la durée à caser, seule information actionnable ici.
  if (!bloc.agent) {
    const quoi = bloc.type === TYPE_INTERVENTION ? "d'intervention" : 'de ménage'
    return { texte: `${formaterDuree(bloc.dureeMin)} ${quoi}`, ton: 'probleme' }
  }
  // 2. L'AGENT EST AILLEURS EN MÊME TEMPS — on le nomme et on compte.
  if (blocsDeLAgentEnConflit && blocsDeLAgentEnConflit.length > 0) {
    const nom = typeof prenomDe === 'function' ? prenomDe(bloc.agent) : bloc.agent
    return { texte: libelleConflit(nom, blocsDeLAgentEnConflit), ton: 'probleme' }
  }
  // 2bis. LE DÉPART DE LA RÉSA A BOUGÉ SANS QUE CE MÉNAGE PUISSE SUIVRE
  // (chantier « le ménage suit sa réservation », 2026-08-14). Le signal est
  // posé par la CASCADE en base (depart_reporte_au) quand elle ne déplace pas :
  // ménage EN COURS (l'agent est sur place — Dimitri tranche au cas par cas) ou
  // ménage FAIT (un séjour = un ménage : la nouvelle date se règle par une
  // intervention posée à la main). Un humain doit trancher → « À régler ».
  if (bloc.run?.depart_reporte_au) {
    const d = new Date(bloc.run.depart_reporte_au + 'T12:00:00')
    const dTxt = Number.isNaN(d.getTime())
      ? bloc.run.depart_reporte_au
      : d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })
    const enCours = bloc.run?.status === 'in_progress'
    return {
      texte: enCours
        ? `départ déplacé au ${dTxt} — ménage en cours conservé`
        : `départ déplacé au ${dTxt} — à replanifier à la main`,
      ton: 'probleme',
    }
  }
  // 3. L'HEURE QU'IL AVAIT FIXÉE A ÉTÉ DÉPLACÉE — presque toujours parce qu'un
  //    horaire accordé a rétréci la plage APRÈS coup. C'est SA décision qui a
  //    bougé : il doit la revoir, ou la confirmer. L'agent, lui, ne voit que
  //    l'heure recalée et n'a rien à arbitrer.
  if (bloc.heureRecalee && Number.isFinite(bloc.debut) && !recalageAcquitte(bloc)) {
    // `action` porte le SEUL motif acquittable — la frise n'a pas à savoir
    // lesquels le sont, c'est la règle qui le dit.
    return { texte: `heure recalée à ${formaterHeureCourte(bloc.debut)}`, ton: 'probleme', action: 'acquitter' }
  }
  // 4. L'HEURE EST PASSÉE ET PERSONNE N'A VALIDÉ.
  //
  // 🔴 CE MOTIF MENT APRÈS 48 H, et c'est pour ça qu'il change de mots (décision
  // Dimitri 2026-08-12). « Non validé » laisse croire qu'on attend l'agent —
  // vrai le jour même, FAUX passé le verrou 48 h : il ne PEUT plus valider (RLS
  // `menage_update_own`). La ligne n'attend alors plus personne, elle attend
  // l'admin, et rien ne le disait. On ne met PAS de bouton « c'est bon » ici :
  // acquitter « non validé » reviendrait à déclarer fait un ménage que personne
  // n'a confirmé — précisément la trace qu'il ne faut pas fabriquer.
  if (estNonValide(bloc, { enRetard, nowMin, position })) {
    return menageRunLockedPourMenage(bloc)
      ? { texte: 'à remplir vous-même — l’agent ne peut plus valider', ton: 'probleme', action: 'corriger' }
      : { texte: 'non validé', ton: 'probleme' }
  }
  // 5. UNE DEMANDE — ce n'est PAS un problème, d'où le ton bleu-gris.
  const phrase = phraseDemande(demande)
  if (phrase) return { texte: phrase, ton: 'demande' }
  return null
}

/**
 * L'HEURE EST PASSÉE ET LE MÉNAGE N'EST PAS VALIDÉ.
 *
 * Deux faits de même nature, et un seul mot pour les dire : le check-in franchi
 * sans validation (`enRetard`, calculé par la vue à partir des alertes) OU le
 * créneau du ménage entièrement écoulé. Le second manquait : un ménage prévu
 * 11 h → 12 h 30, non validé à 14 h, ne déclenchait aucune alerte tant que le
 * voyageur suivant n'était pas attendu — alors que c'est exactement le moment
 * où l'appeler sert encore à quelque chose.
 */
export function estNonValide(bloc, { enRetard = false, nowMin, position = 'aujourdhui' } = {}) {
  if (!bloc || bloc.termine) return false
  if (enRetard) return true
  return avancementPct(bloc, nowMin, position) >= 100
}

/** Combien de lignes demandent quelque chose — le seul compteur d'alerte du bandeau. */
export function compterMotifs(blocs, {
  conflits, conflitsParAgent, estEnRetard, prenomDe, demandeDe, nowMin, position = 'aujourdhui',
} = {}) {
  const list = Array.isArray(blocs) ? blocs : []
  const retard = typeof estEnRetard === 'function' ? estEnRetard : () => false
  const dem = typeof demandeDe === 'function' ? demandeDe : () => null
  return list.filter((b) => motifDeLigne(b, {
    enRetard: b.run ? retard(b.run) : false,
    // ⚠️ LE CONFLIT EST CELUI DU BLOC, PAS CELUI DE L'AGENT — sans `conflits`,
    // les trois ménages d'un agent en conflit sur deux d'entre eux comptaient
    // tous les trois, et le bandeau annonçait plus d'alertes qu'il n'y en avait.
    blocsDeLAgentEnConflit: conflits && conflits.has(b.id) && conflitsParAgent
      ? conflitsParAgent.get(b.agent)
      : null,
    prenomDe, demande: dem(b), nowMin, position,
  }) != null).length
}

// ── LES GROUPES — la journée triée par URGENCE, pas par heure ───────────────

/**
 * ⚠️ LE REGROUPEMENT REMPLACE LE FOND ROSÉ PAR LIGNE (maquette 2026-08-11).
 *
 * Constat à l'écran la veille : sept lignes sur huit étaient rosées. Un signal
 * porté par presque toutes les lignes ne signale plus rien — il colore. Le rosé
 * remonte donc d'un cran, sur le TITRE du groupe : il y a un endroit rosé sur
 * la page, et ce qu'il contient est ce sur quoi il faut agir.
 *
 * L'ordre n'est pas décoratif — c'est l'ordre dans lequel on traite la journée :
 * ce qui est cassé, ce qu'on nous demande, ce qui tourne, ce qui vient, ce qui
 * est fini.
 */
export const GROUPES = [
  { cle: 'a_regler', titre: 'À régler' },
  { cle: 'demande', titre: 'Demandes en attente' },
  { cle: 'en_cours', titre: 'En cours' },
  { cle: 'a_venir', titre: 'À venir' },
  { cle: 'valide', titre: 'Validés' },
]

/** Le groupe d'un bloc. Un bloc n'est JAMAIS dans deux groupes : le premier vrai gagne. */
/**
 * LE RECALAGE A-T-IL ÉTÉ ACQUITTÉ — et POUR CETTE HEURE-LÀ ?
 *
 * 🔴 ON COMPARE L'HEURE, PAS UN BOOLÉEN (décision Dimitri 2026-08-12). Un simple
 * « accepté » ferait taire AUSSI un recalage futur et DIFFÉRENT : on aurait
 * acquitté 13:00, un nouvel accord ramènerait à 15:00, et plus personne ne le
 * verrait. En comparant, la ligne se tait tant que le recalage vaut l'heure
 * acquittée — et le motif REVIENT dès qu'il change.
 * CASSER POUR VÉRIFIER : rendre `accepte != null` → un second recalage passe muet.
 */
/**
 * LE VERROU 48 H EST-IL TOMBÉ SUR CE MÉNAGE ?
 *
 * Miroir JS de la RLS `menage_update_own` (ADR-207/208) : passé minuit Paris de
 * J+2, l'agent ne peut PLUS écrire. Le motif doit cesser de l'attendre.
 * Sans date lisible → on ne conclut pas (le motif reste « non validé »).
 */
export function menageRunLockedPourMenage(bloc, maintenant) {
  const iso = bloc?.run?.scheduled_date
  if (typeof iso !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return false
  const [y, m, d] = iso.split('-').map(Number)
  const limite = Date.UTC(y, m - 1, d + 2)
  const ref = maintenant instanceof Date ? maintenant.getTime() : Date.now()
  return ref >= limite
}

export function recalageAcquitte(bloc) {
  const a = bloc?.run?.recalage_accepte_a
  if (!Number.isFinite(a)) return false
  return Number.isFinite(bloc?.debut) && a === bloc.debut
}

export function groupeDuBloc(bloc, {
  enRetard = false, enConflit = false, demande = null, nowMin, position = 'aujourdhui',
} = {}) {
  if (!bloc) return 'a_venir'
  if (bloc.termine) return 'valide'
  if (!bloc.agent) return 'a_regler'
  if (enConflit) return 'a_regler'
  // Une heure déplacée sans qu'il l'ait voulu appelle une décision : la ligne
  // remonte avec ce qui est cassé, pas avec ce qui se déroule normalement.
  if (bloc.heureRecalee && !recalageAcquitte(bloc)) return 'a_regler'
  if (estNonValide(bloc, { enRetard, nowMin, position })) return 'a_regler'
  if (phraseDemande(demande)) return 'demande'
  const pct = avancementPct(bloc, nowMin, position)
  return pct > 0 ? 'en_cours' : 'a_venir'
}

/**
 * ⚠️ REPLIÉS PAR DÉFAUT AU-DELÀ DE 3. « À venir » et « Validés » sont les deux
 * groupes qui n'appellent aucune action : les laisser ouverts repousserait hors
 * de l'écran les trois groupes qui, eux, en appellent une. En dessous de 4
 * lignes, replier coûte un clic pour rien.
 */
export function replieParDefaut(cle, nb) {
  return (cle === 'a_venir' || cle === 'valide') && nb > 3
}

/** Groupe et ORDONNE : par groupe, puis par heure de début, puis par id (ordre stable). */
export function repartirEnGroupes(blocs, opts = {}) {
  const list = Array.isArray(blocs) ? blocs : []
  const par = new Map(GROUPES.map((g) => [g.cle, []]))
  for (const b of list) {
    const cle = groupeDuBloc(b, typeof opts.pour === 'function' ? opts.pour(b) : opts)
    par.get(cle).push(b)
  }
  return GROUPES.map((g) => ({
    ...g,
    blocs: par.get(g.cle).sort((a, b) => a.debut - b.debut || String(a.id).localeCompare(String(b.id))),
  })).filter((g) => g.blocs.length > 0)
}

// ── Récaps de la colonne de droite ──────────────────────────────────────────

/** Total d'heures de travail d'un agent — le chiffre qui manquait le plus. */
export function heuresDeTravail(blocs) {
  return (Array.isArray(blocs) ? blocs : []).reduce(
    (t, b) => t + (Number(b?.dureeMin) > 0 ? Number(b.dureeMin) : 0), 0
  )
}

/**
 * Qui est libre pour un créneau donné.
 *
 * Un agent « complet » n'est pas un agent absent : il travaille déjà à cette
 * heure-là. On le montre grisé plutôt que de le masquer — le masquer ferait
 * croire qu'il n'existe pas, et on le chercherait.
 *
 * @returns {Array<{email:string, libre:boolean}>}
 */
export function agentsDisponibles(blocs, agents, { debut, fin } = {}) {
  const list = Array.isArray(blocs) ? blocs : []
  return (Array.isArray(agents) ? agents : []).map((email) => {
    const siens = list.filter((b) => b?.agent === email)
    const occupe = siens.some((b) => b.debut < fin && debut < b.fin)
    return { email, libre: !occupe }
  })
}

// ── Construction des blocs ──────────────────────────────────────────────────

/**
 * Transforme runs + interventions en BLOCS positionnés, prêts à rendre.
 *
 * `heuresFixees` = { [id]: "HH:MM" } — l'ordre que l'orchestrateur a posé à la
 * souris. Absent ⇒ le bloc se cale au début de son créneau.
 *
 * Un bloc TERMINÉ n'est pas déplaçable (c'est fait), un bloc SANS AGENT non
 * plus (il faut d'abord quelqu'un pour le faire — déplacer le travail de
 * personne n'a pas de sens).
 */
export function construireBlocs({
  runs = [],
  interventions = [],
  logementsById = {},
  horaires = {},
  heuresFixees = {},
  guestTimes = {},
} = {}) {
  const blocs = []

  for (const run of Array.isArray(runs) ? runs : []) {
    if (!run || run.status === 'skipped') continue
    const logement = logementsById[run.logement_id] || null
    const h = horaires[run.logement_id] || {}
    // ⚠️ LES BORNES SUIVENT LE RÉEL, PAS LE CONTRAT (bug Dimitri 2026-08-16) :
    // départ réel = l'heure annoncée au formulaire si elle existe, sinon le
    // contrat — idem pour l'arrivée. Les ACCORDÉS arrivent déjà par le guichet
    // (h.checkout/h.checkin). Une demande EN ATTENTE ne change rien. La plage
    // blanche, le glisser, le tracer et les poignées partagent CE créneau :
    // une seule vérité, celle qui est dessinée.
    const gt = guestTimes[run.id] || {}
    const creneau = creneauPossible({
      checkout: gt.departure || h.checkout,
      checkin: gt.arrival || h.checkin,
    })
    const dureeMin = dureeDuMenage(run, logement)
    // EXCEPTION DE DURÉE : « 4 h au lieu de 1 h 30 ». Une bordure et une phrase,
    // pas un symbole ni une entrée de légende — un pictogramme de plus obligerait
    // à consulter la légende pour comprendre une information qui tient en cinq mots.
    const dureeParDefaut = dureeParDefautDuLogement(logement)
    const exceptionDuree = dureeMin !== dureeParDefaut
    const heureFixee = heuresFixees[run.id]
    const place = placerBloc({ heureFixee, creneau, duree: dureeMin })
    const { debut, fin } = place
    const termine = run.status === 'completed'
    blocs.push({
      id: run.id,
      type: TYPE_MENAGE,
      run,
      logementId: run.logement_id,
      nom: logement?.nom || run.logement_id,
      agent: run.assigned_provider_email || null,
      statut: run.status || 'scheduled',
      dureeMin,
      dureeParDefaut,
      exceptionDuree,
      creneau,
      debut,
      fin,
      termine,
      // Heure de FIN réelle d'un ménage validé — « validé 11:40 » dit quand
      // c'est fini, ce que le créneau prévu ne dit pas.
      finReelle: termine && run.completed_at
        ? formaterHeure(new Date(run.completed_at).getHours() * 60 + new Date(run.completed_at).getMinutes())
        : null,
      // ⚠️ « HEURE À FIXER » est un état DISTINCT de « sans agent » : un agent a
      // été choisi mais personne n'a encore dit QUAND. Sans ce booléen, le bloc
      // calé par défaut au début du créneau ressemblerait à une décision prise.
      aHeureFixee: minutesDeHeure(heureFixee) != null,
      // ⚠️ L'HEURE A ÉTÉ DÉPLACÉE PAR RAPPORT À CE QUI AVAIT ÉTÉ DÉCIDÉ —
      // presque toujours parce qu'un horaire accordé a rétréci la plage APRÈS
      // coup. C'est l'orchestrateur qui doit le savoir, pas l'agent.
      heureRecalee: place.recale,
      heureInitiale: place.initiale,
      // Un ménage fait ne bouge plus ; un ménage sans agent attend un agent.
      deplacable: !termine && !!run.assigned_provider_email,
    })
  }

  for (const it of Array.isArray(interventions) ? interventions : []) {
    if (!it || !it.id) continue
    const dureeMin = Number(it.duree_min) > 0 ? Number(it.duree_min) : DUREE_DEFAUT_MIN
    // ⚠️ AUCUNE PLAGE NE BORNE UNE INTERVENTION. Elle ne vient d'aucune
    // réservation : elle peut se poser à 8 h comme à 19 h. `sansPlage` dit au
    // rendu de ne PAS dessiner le fond de disponibilité derrière elle — un fond
    // qui n'existe pas ne doit pas être suggéré.
    const creneau = { debut: FRISE_DEBUT_MIN, fin: FRISE_FIN_MIN, degrade: false, sansPlage: true }
    const heureFixeeIt = heuresFixees[it.id] || it.heure_fixee || it.debut
    const { debut, fin } = placerBloc({ heureFixee: heureFixeeIt, creneau, duree: dureeMin })
    blocs.push({
      id: it.id,
      type: TYPE_INTERVENTION,
      run: null,
      intervention: it,
      logementId: it.logement_id || null,
      // PERSONNELLE MASQUÉE (2026-08-17) : la vue serveur a blanchi le
      // libellé chez l'admin → le bloc dit « Indisponible », rien d'autre,
      // et `masque` le rend intouchable à l'écran (le serveur refuse déjà).
      masque: it.personnel === true && !it.libelle,
      nom: it.personnel === true && !it.libelle ? 'Indisponible' : (it.libelle || 'Intervention'),
      // ⚠️ `assigned_provider_email` — LE NOM DE LA COLONNE, celui que lit
      // `chargerInterventions` et qu'écrit `majIntervention`. On lisait ici
      // `agent_email`, qui n'existe nulle part : TOUTE intervention arrivait
      // donc sans agent, se peignait en « Cliquer pour assigner » et gonflait
      // le compteur d'alertes du bandeau. Un champ absent ne lève aucune
      // erreur — il rend `undefined`, et `undefined` se lit « personne ».
      agent: it.assigned_provider_email || null,
      // 🔴 L'ÉTAT VIENT DE LA DONNÉE, PLUS DU CODE. `'scheduled'` et
      // `termine: false` étaient EN DUR : l'agent validait sa livraison,
      // la base portait `completed` + `terminee_par`, et la frise repeignait
      // un bloc « à faire » — le retour du terrain mourait ici (mesuré au
      // scénario de communication, 2026-08-14).
      statut: it.statut || 'scheduled',
      dureeMin,
      creneau,
      debut,
      fin,
      termine: it.statut === 'completed',
      // « validé 12:30 » — même langage que les ménages, même colonne posée
      // par trigger (`terminee_le`), jamais par le client.
      finReelle: it.statut === 'completed' && it.terminee_le
        ? formaterHeure(new Date(it.terminee_le).getHours() * 60 + new Date(it.terminee_le).getMinutes())
        : null,
      aHeureFixee: minutesDeHeure(heureFixeeIt) != null,
      deplacable: it.statut !== 'completed' && !!it.assigned_provider_email,
    })
  }

  return blocs
}

/**
 * RÉPARTIT LES BLOCS D'UN COULOIR EN SOUS-VOIES.
 *
 * Défaut constaté à l'écran le 2026-08-10 : deux ménages qui se chevauchent
 * étaient dessinés au MÊME endroit et se recouvraient — le second passait
 * littéralement sous le premier. On voyait donc qu'il y avait un problème
 * (bordures rouges) sans pouvoir voir LEQUEL, ce qui est le pire des deux
 * mondes : l'alerte sans l'information.
 *
 * Empilement glouton : chaque bloc va dans la PREMIÈRE voie encore libre à son
 * heure de début. Les journées sans chevauchement gardent donc une seule voie,
 * et la frise ne s'épaissit que là où quelque chose ne va pas.
 *
 * @returns {{ blocs: Array, nbVoies: number }} blocs enrichis d'un `voie` (0-indexé).
 */
export function repartirEnVoies(blocs) {
  const list = (Array.isArray(blocs) ? blocs : [])
    .filter(b => b && Number.isFinite(b.debut) && Number.isFinite(b.fin))
    .slice()
    .sort((a, b) => a.debut - b.debut || String(a.id).localeCompare(String(b.id)))
  const finDeVoie = []
  const out = []
  for (const b of list) {
    let voie = finDeVoie.findIndex((fin) => fin <= b.debut)
    if (voie === -1) { finDeVoie.push(b.fin); voie = finDeVoie.length - 1 }
    else finDeVoie[voie] = b.fin
    out.push({ ...b, voie })
  }
  return { blocs: out, nbVoies: Math.max(1, finDeVoie.length) }
}

/**
 * ORDRE DE LA JOURNÉE D'UN AGENT — la règle partagée entre les deux écrans.
 *
 * L'orchestrateur fixe l'ordre dans la frise ; l'agent doit voir SA journée
 * dans CET ordre-là. Si les deux écrans triaient chacun de leur côté, ils
 * finiraient par diverger — et l'agent exécuterait un ordre que personne n'a
 * décidé. La règle vit donc ici, une seule fois.
 *
 * Priorité : heure FIXÉE par l'orchestrateur → début du créneau possible →
 * l'identifiant, pour que deux ménages sans repère gardent un ordre stable
 * d'un rendu à l'autre (un ordre qui bouge tout seul ne se suit pas).
 *
 * @param {Array} runs
 * @param {{ heureFixeeDe?: Function, debutCreneauDe?: Function }} opts
 */
export function ordonnerJournee(runs, { heureFixeeDe, debutCreneauDe } = {}) {
  const fixee = typeof heureFixeeDe === 'function' ? heureFixeeDe : (r) => r?.heure_fixee
  const creneau = typeof debutCreneauDe === 'function' ? debutCreneauDe : () => null

  const rang = (r) => {
    const h = minutesDeHeure(fixee(r))
    if (h != null) return h
    const c = minutesDeHeure(creneau(r))
    if (c != null) return c
    return Number.MAX_SAFE_INTEGER
  }

  return (Array.isArray(runs) ? runs : []).slice().sort((a, b) => {
    const ra = rang(a)
    const rb = rang(b)
    if (ra !== rb) return ra - rb
    return String(a?.id).localeCompare(String(b?.id))
  })
}

/** Regroupe les blocs par agent. Les sans-agent tombent dans la clé SANS_AGENT. */
export const SANS_AGENT = '(sans agent)'
export function grouperParAgent(blocs, ordreAgents = []) {
  const par = new Map()
  for (const email of ordreAgents) if (email) par.set(email, [])
  for (const b of Array.isArray(blocs) ? blocs : []) {
    const cle = b?.agent || SANS_AGENT
    if (!par.has(cle)) par.set(cle, [])
    par.get(cle).push(b)
  }
  // SANS_AGENT toujours EN DERNIER : c'est une file d'attente, pas un agent.
  const entrees = [...par.entries()].filter(([k]) => k !== SANS_AGENT)
  if (par.has(SANS_AGENT)) entrees.push([SANS_AGENT, par.get(SANS_AGENT)])
  for (const [, list] of entrees) list.sort((a, b) => a.debut - b.debut || String(a.id).localeCompare(String(b.id)))
  return entrees
}

export { TYPE_MENAGE, TYPE_INTERVENTION }

// ── LE LANGAGE DES OCCUPATIONS (report Demandes → Suivi, 2026-08-16) ────────

/**
 * UNE LIGNE « À HISTOIRE » : celle qui porte une annonce du formulaire, une
 * demande d'horaire en attente, ou un conflit. Les zones d'occupation et les
 * cartes de bord ne s'affichent QUE sur ces lignes — au-delà de ~7-8 lignes
 * ornées, le gris devient du bruit de fond (analyse de densité 2026-08-16,
 * seuil validé Dimitri).
 */
export function ligneAHistoire({ demande, annonceArriveeFormulaire, annonceDepartMin, enConflit } = {}) {
  return !!demande || !!annonceArriveeFormulaire || Number.isFinite(annonceDepartMin) || !!enConflit
}

/**
 * CE QUE PORTE UNE CARTE DE BORD — et si elle tient dans sa zone.
 *
 * LA RÈGLE (Dimitri 2026-08-16) : la carte ne sort JAMAIS de sa zone. Quand le
 * texte ne rentre pas, elle se vide et ne garde que son icône, centrée —
 * jamais de troncature à trois points, jamais de débordement sur le bloc.
 *
 * `mesureTexte(texte)` est injectée (canvas au rendu, largeur fixe en test) :
 * la décision reste pure et se teste sans DOM.
 */
export function contenuCarteBord({ prenom, personnes, heure, largeurZonePx, mesureTexte,
  iconePx = 9, gapPx = 4, paddingPx = 10, margePx = 4 } = {}) {
  // DÉGRADATION PRIORISÉE (option A, Dimitri 2026-08-16) : quand la zone
  // rétrécit, le prénom tombe d'abord, puis le nombre — L'HEURE RESTE JUSQU'AU
  // BOUT. Icône seule uniquement quand même l'heure ne tient pas.
  // « pers. » fait partie du segment (Dimitri 2026-08-17 : « un chiffre seul
  // ne dit rien ») — il TOMBE AVEC le nombre dans la dégradation, jamais de
  // nombre nu. Le seuil suit tout seul : la mesure porte sur le texte réel.
  const n = Number.isFinite(personnes) && personnes > 0 ? `${personnes} pers.` : null
  const h = heure || null
  // Correction Dimitri 2026-08-16 : l'ICÔNE ET L'HEURE RESTENT ENSEMBLE
  // jusqu'au bout — sans l'icône, « 10h » ne dit pas si l'on part ou arrive.
  // L'ordre de chute : prénom, puis nombre, puis rien.
  const candidats = [
    [prenom, n, h], // tout
    [n, h],         // sans prénom
    [h],            // icône + heure — indissociables, jusqu'au bout
  ].map((m) => m.filter(Boolean).join(' · ')).filter(Boolean)
  if (!candidats.length) return { mode: 'icone', texte: '' }
  const tient = (texte) => {
    const largeurCarte = iconePx + gapPx + (mesureTexte ? mesureTexte(texte) : texte.length * 6) + paddingPx
    return Number.isFinite(largeurZonePx) && largeurZonePx - margePx >= largeurCarte
  }
  const vus = new Set()
  for (const texte of candidats) {
    if (vus.has(texte)) continue
    vus.add(texte)
    if (tient(texte)) return { mode: 'texte', texte }
  }
  // Avec une heure : on la garde MÊME si ça frotte (elle est la raison d'être
  // de la carte) ; sans heure : icône seule.
  if (h) return { mode: 'texte', texte: h }
  return { mode: 'icone', texte: candidats[0] }
}

/**
 * L'ORDRE DE LA LISTE (Dimitri 2026-08-16) : les lignes À PROBLÈME d'abord
 * (pas validé, sans agent, conflit), le reste dans l'ordre habituel — et
 * L'ORDRE EST FIGÉ AU CHARGEMENT : valider un ménage le laisse À SA PLACE,
 * un passage en retard ne le fait pas remonter (la couleur change, c'est
 * tout). Le tri ne se recalcule qu'au changement de jour ou au rechargement.
 *
 * Pure : rend une Map id → rang. Les blocs INCONNUS d'une mémoire existante
 * s'ajoutent en fin, sans déplacer les connus.
 */
export function rangsDeListe({ blocs = [], etage = () => 1, cleDe = () => [0, ''], rangs = null } = {}) {
  // TROIS ÉTAGES (précision Dimitri) : 0 = rouge (problème), 1 = bleu (reste
  // à faire), 2 = vert (réglé). À l'intérieur : l'ordre habituel.
  const base = blocs.slice().sort((a, b) => {
    const ua = etage(a)
    const ub = etage(b)
    if (ua !== ub) return ua - ub
    const ka = cleDe(a), kb = cleDe(b)
    return ka[0] - kb[0] || String(ka[1]).localeCompare(String(kb[1]), 'fr')
  })
  if (!rangs) {
    const out = new Map()
    base.forEach((b, i) => out.set(b.id, i))
    return out
  }
  const out = new Map(rangs)
  for (const b of base) if (!out.has(b.id)) out.set(b.id, out.size)
  return out
}
